import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Download, Filter, Mail } from 'lucide-react';
import { getSubmissionsByUser, fetchExecutives, sendReport } from '../../../Services/salesapis';
import Hoc from "../../../components/HOC";
import Loader from "../../../components/Loader";
import CustomToolTip from '../../../components/ToolTip';
import MuiDatePicker from '../../../components/MuiDatePicker';
import moment from 'moment';
import CustomDropdown from '../../../components/CustomDropdown';
import { toast } from 'react-toastify';
import Swal from "sweetalert2";
import { getValuesFromLocalstorage } from '../../../components/Helpers';
import LOCAL_KEYS from '../../../helpers/localstorage';

const SubmissionsTable = () => {
  // Table filters and state
  const [submittedByFilter, setSubmittedByFilter] = useState('');
  const [timeframeFilter, setTimeframeFilter] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [executives, setExecutives] = useState([]);
  const [executivesLoading, setExecutivesLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const fetchGuardRef = useRef(false); // Prevent double API call
  const [toastShown, setToastShown] = useState(false); // Prevent duplicate toast

  const userDetails = getValuesFromLocalstorage(LOCAL_KEYS.USER_DETAILS);
  const isAdmin = userDetails?.acctType === 'ADMIN';
  const userFullName = `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim();

  // If not admin, set submittedBy filter to user's name and disable changing it
  useEffect(() => {
    if (!isAdmin && userFullName) {
      setSubmittedByFilter(userFullName);
    }
  }, [isAdmin, userFullName]);

  // Fetch executives for dropdown
  const fetchExecutivesData = async () => {
    try {
      setExecutivesLoading(true);
      const executivesData = await fetchExecutives();
      setExecutives(executivesData || []);
    } catch (err) {
      setExecutives([]);
    } finally {
      setExecutivesLoading(false);
    }
  };

  // Fetch data from API
  const fetchData = async (page = 0) => {
    if (fetchGuardRef.current) return; // Guard against double call
    fetchGuardRef.current = true;
    try {
      setLoading(true);
      let timeframe = null;
      let fromDate = null;
      let toDate = null;
      if (timeframeFilter === 'today') {
        timeframe = 'today';
      } else if (timeframeFilter === 'thisweek') {
        timeframe = 'thisweek';
      } else if (timeframeFilter === 'thismonth') {
        timeframe = 'thismonth';
      } else if (timeframeFilter === 'custom') {
        if (startDate && endDate) {
          fromDate = startDate;
          toDate = endDate;
        } else {
          setLoading(false);
          fetchGuardRef.current = false;
          return; // Don't call API if custom but no dates
        }
      }
      // Only admins can use 'All Users' (empty) filter
      const submittedByParam = isAdmin ? submittedByFilter : userFullName;
      const response = await getSubmissionsByUser(page, pageSize, timeframe, fromDate, toDate, submittedByParam);
      setData(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
      setCurrentPage(response.number || 0);
      setError(null); // Clear error on success
      setToastShown(false); // Reset toast flag on success
    } catch (err) {
      setError(err.message);
      if (!toastShown) {
        toast.dismiss(); // Dismiss any existing toasts
        toast.error(err.message || 'Failed to fetch submissions by user');
        setToastShown(true);
      }
      setData([]);
      setTotalPages(0);
      setTotalElements(0);
      setCurrentPage(0);
    } finally {
      setLoading(false);
      setTimeout(() => { fetchGuardRef.current = false; }, 0); // Allow next call in next tick
    }
  };

  useEffect(() => {
    fetchExecutivesData();
  }, []);

  useEffect(() => {
    fetchData(currentPage);
    // eslint-disable-next-line
  }, [currentPage, pageSize, timeframeFilter, startDate, endDate, submittedByFilter]);

  // Filter data based on selected filters (for local filtering if needed)
  const filteredData = useMemo(() => {
    return data; // Since we're doing server-side filtering, return data as-is
  }, [data]);

  const sortedData = useMemo(() => {
    let sortable = [...filteredData];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aValue = (a[sortConfig.key] || '').toString().toLowerCase();
        const bValue = (b[sortConfig.key] || '').toString().toLowerCase();
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredData, sortConfig]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframeFilter(newTimeframe);
    setCurrentPage(0);
    if (newTimeframe !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleSubmittedByChange = (newSubmittedBy) => {
    setSubmittedByFilter(newSubmittedBy);
    setCurrentPage(0);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const getTimeframeLabel = () => {
    if (timeframeFilter === 'today') return 'today';
    if (timeframeFilter === 'thisweek') return 'thisweek';
    if (timeframeFilter === 'thismonth') return 'thismonth';
    if (timeframeFilter === 'custom') {
      if (startDate && endDate) {
        return `${moment(startDate).format('DD MMM YYYY')} - ${moment(endDate).format('DD MMM YYYY')}`;
      }
      return 'Custom Date Range';
    }
    return 'Selected Timeframe';
  };

  const getSubmittedByLabel = () => {
    if (isAdmin) return submittedByFilter ? submittedByFilter : 'all';
    return userFullName || 'User';
  };

  const handleSendReport = async () => {
    const timeframeLabel = getTimeframeLabel();
    const submittedByLabel = getSubmittedByLabel();

    const res = await Swal.fire({
      title: 'Are you sure?',
      text: `Are you sure you want to send the report to Mr. Sudhir for ${timeframeLabel}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#201F1E',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, send report',
      cancelButtonText: 'No, cancel',
    });

    if (!res.isConfirmed) 
      return 

    try {
       const response = await sendReport(submittedByLabel, timeframeLabel, startDate, endDate);
      toast.success(response?.message || 'Report sent successfully.');
    } catch (error) {
      toast.error(error?.message || 'Failed to send report.');
    }
  };

  // Pagination helpers
  const getPaginationInfo = () => {
    // When no records, show 0 to 0
    if (totalElements === 0) {
      return { start: 0, end: 0 };
    }
    const start = currentPage * pageSize + 1;
    const end = Math.min((currentPage + 1) * pageSize, totalElements);
    return { start, end };
  };
  
  const getPageNumbers = () => {
    const totalPageCount = totalPages;
    let pages = [];
    if (totalPageCount <= 5) {
      for (let i = 1; i <= totalPageCount; i++) {
        pages.push(i);
      }
      return pages;
    }
    if (currentPage + 1 <= 3) {
      return [1, 2, 3, 4, 5];
    } else if (currentPage + 1 >= totalPageCount - 2) {
      return [
        totalPageCount - 4,
        totalPageCount - 3,
        totalPageCount - 2,
        totalPageCount - 1,
        totalPageCount
      ];
    } else {
      return [
        currentPage - 1,
        currentPage,
        currentPage + 1,
        currentPage + 2,
        currentPage + 3
      ].map(x => x + 1);
    }
  };
  
  const { start, end } = getPaginationInfo();
  const pageNumbers = getPageNumbers();
  const totalPageCount = totalPages;

  // Show loader during initial load
  if (loading && data.length === 0 && executives.length === 0) {
    return <Loader isLoading={true} />;
  }

  return (
    <div className="bg-white">
      <div className="w-full p-6 h-full">
        {/* Data Table */}
        <div className="bg-white border border-gray-200 relative overflow-hidden rounded-md">
          {/* Table Loading Overlay */}
          {loading && <Loader isLoading={true} />}
          <div className="p-6 sm:p-4 border-b border-gray-200 ">
            {isAdmin && (
            <div className="flex flex-row items-center justify-end gap-2 mb-4">
              <button
                onClick={handleSendReport}
                type="button"
                className="flex items-center space-x-2 px-3 py-2 sm:px-4 sm:py-2 bg-[#201F1E] text-white rounded-lg hover:bg-[#201F1E] transition-colors text-sm sm:text-base"
              >
                <Mail className="w-5 h-5" />
                
                <span>Send Report</span>
              </button>
            </div>
            )}
            {/* Table Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
              {isAdmin ? (
                <div>
                  <CustomDropdown
                    value={executives.find(e => (e.name || e.fullName || e.userName || e) === submittedByFilter) ? { label: submittedByFilter || 'All Users', value: submittedByFilter } : { label: 'All Users', value: '' }}
                    name="submittedBy"
                    handleChange={option => handleSubmittedByChange(option.value)}
                    placeholder="Submitted By"
                    options={[
                      { label: 'All Users', value: '' },
                      ...executives.map(e => ({
                        label: e.name || e.fullName || e.userName || e,
                        value: e.name || e.fullName || e.userName || e
                      }))
                    ]}
                    handleGetOptionValue={opt => opt.value}
                    handleGetOptionName={opt => opt.label}
                    isDisabled={executivesLoading}
                    customClasses="w-full"
                  />
                </div>
              ) : (
                <div>
                  <CustomDropdown
                    value={{ label: userFullName, value: userFullName }}
                    name="submittedBy"
                    handleChange={() => {}}
                    placeholder="Submitted By"
                    options={[{ label: userFullName, value: userFullName }]}
                    handleGetOptionValue={opt => opt.value}
                    handleGetOptionName={opt => opt.label}
                    isDisabled={true}
                    customClasses="w-full opacity-60 cursor-not-allowed"
                    hideDropdownIndicator={true}
                  />
                </div>
              )}
              <div>
                <CustomDropdown
                  value={{ label: timeframeFilter === 'today' ? 'Today' : timeframeFilter === 'thisweek' ? 'This Week' : timeframeFilter === 'thismonth' ? 'This Month' : 'Custom Date Range', value: timeframeFilter }}
                  name="timeframe"
                  handleChange={option => handleTimeframeChange(option.value)}
                  placeholder="Timeframe"
                  options={[
                    {label: 'Today', value: 'today'},
                    { label: 'This Week', value: 'thisweek' },
                    { label: 'This Month', value: 'thismonth' },
                    { label: 'Custom Date Range', value: 'custom' }
                  ]}
                  handleGetOptionValue={opt => opt.value}
                  handleGetOptionName={opt => opt.label}
                  customClasses="w-full"
                />
              </div>
              {timeframeFilter === 'custom' && (
                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <MuiDatePicker
                      label="From Date"
                      value={startDate ? moment(startDate) : null}
                      onChange={date => setStartDate(date ? date.format('YYYY-MM-DD') : '')}
                      maxDate={endDate ? moment(endDate) : null}
                    />
                  </div>
                  <div>
                    <MuiDatePicker
                      label="To Date"
                      value={endDate ? moment(endDate) : null}
                      onChange={date => setEndDate(date ? date.format('YYYY-MM-DD') : '')}
                      minDate={startDate ? moment(startDate) : null}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
            {sortedData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4 rounded-lg text-center">
                <p className="text-sm font-medium text-gray-700">
                No records for the selected filter.
                </p>
                
              </div>
            ) : (
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider cursor-pointer whitespace-nowrap" onClick={() => handleSort('consultantName')}>
                    Consultant Name
                    {sortConfig.key === 'consultantName' && (
                      <span className="ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider cursor-pointer whitespace-nowrap" onClick={() => handleSort('clientName')}>
                    Client Name
                    {sortConfig.key === 'clientName' && (
                      <span className="ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider cursor-pointer whitespace-nowrap" onClick={() => handleSort('clientLocation')}>
                    Location
                    {sortConfig.key === 'clientLocation' && (
                      <span className="ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider cursor-pointer whitespace-nowrap" onClick={() => handleSort('vendor')}>
                    Vendor
                    {sortConfig.key === 'vendor' && (
                      <span className="ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider whitespace-nowrap">Technology</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider whitespace-nowrap">Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider whitespace-nowrap" style={{ maxWidth: '120px', width: '120px' }}>Notes</th>
                  {submittedByFilter === '' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider whitespace-nowrap">Submitted By</th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider whitespace-nowrap">Submitted Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedData.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.consultantName || ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.clientName || ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.clientLocation || ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.vendor || ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {item.technology}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${item.rate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ maxWidth: '120px', width: '120px' }}>
                      <div 
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                        data-tip={item.notes}
                        data-for={`notesTip-${index}`}
                        title={item.notes} // Fallback for basic browser tooltip
                      >
                        {item.notes || ''}
                      </div>
                      {item.notes && <CustomToolTip id={`notesTip-${index}`} title={item.notes} />}
                    </td>
                    {submittedByFilter === '' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.submittedBy}</td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(item.submittedDate).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.stsCd === 'A' ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-[#34D39920] text-[#34D399]">Active</span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-50 text-red-400">Closed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
          {/* Pagination */}

          {sortedData.length > 0 && (
          <div className="bg-white px-2 sm:px-4 py-2 sm:py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 gap-2">
            <div className="flex-1 flex justify-between w-full sm:hidden">
              <button 
                onClick={() => currentPage > 0 && handlePageChange(0)}
                disabled={currentPage === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button 
                onClick={() => currentPage > 0 && handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => currentPage < totalPageCount - 1 && handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPageCount - 1 || totalElements === 0}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => currentPage < totalPageCount - 1 && handlePageChange(totalPageCount - 1)}
                disabled={currentPage === totalPageCount - 1 || totalElements === 0}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between w-full">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{start}</span> to <span className="font-medium">{end}</span> of <span className="font-medium">{totalElements}</span> results
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="rows-per-page" className="text-sm text-gray-700">
                    Rows per page:
                  </label>
                <select
                    id="rows-per-page"
                  value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                </select>
              </div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => currentPage > 0 && handlePageChange(0)}
                    disabled={currentPage === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    First
                  </button>
                <button
                    onClick={() => currentPage > 0 && handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                  {pageNumbers.map(pageNum => (
                      <button
                        key={pageNum}
                      onClick={() => handlePageChange(pageNum - 1)}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 ${
                        pageNum - 1 === currentPage
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-white text-gray-700'
                      } text-sm font-medium hover:bg-gray-50`}
                      >
                      {pageNum}
                      </button>
                  ))}
                <button
                    onClick={() => currentPage < totalPageCount - 1 && handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPageCount - 1 || totalElements === 0}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                  <button
                    onClick={() => currentPage < totalPageCount - 1 && handlePageChange(totalPageCount - 1)}
                    disabled={currentPage === totalPageCount - 1 || totalElements === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Last
                  </button>
                </nav>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Hoc(SubmissionsTable); 